import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Users, ChevronDown, ChevronUp, Loader2, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';
const labelCls = 'block text-xs font-medium text-text-secondary mb-1';

interface Criterios {
  medicoId?: string;
  genero?: string;
  edadMin?: number | '';
  edadMax?: number | '';
  ultimaCitaDesde?: string;
  ultimaCitaHasta?: string;
}

const GENERO_OPTIONS = [
  { value: '', label: 'Todos los géneros' },
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino', label: 'Femenino' },
  { value: 'otro', label: 'Otro' },
  { value: 'no_especificado', label: 'No especificado' },
];

export function CampanaFormPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showFiltros, setShowFiltros] = useState(false);
  const [enviando, setEnviando] = useState<'ahora' | 'programado'>('ahora');
  const [previewCount, setPreviewCount] = useState<{ total: number; conEmail: number; preview: any[] } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [form, setForm] = useState({
    nombre: '',
    plantillaId: '',
    fechaProgramada: '',
  });
  const [criterios, setCriterios] = useState<Criterios>({});

  const { data: plantillas = [] } = useQuery({
    queryKey: ['plantillas-marketing'],
    queryFn: () => api.get('/api/marketing/plantillas').then(r => r.data),
  });
  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos'],
    queryFn: () => api.get('/api/medicos').then(r => r.data).catch(() => []),
  });

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post('/api/marketing/campanas', dto).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campanas'] });
      toast.success('Campaña creada');
      navigate(`/marketing/campanas/${data.id}`);
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al crear campaña')),
  });

  const enviarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/marketing/campanas/${id}/enviar`).then(r => r.data),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['campanas'] });
      toast.success(`Enviando a ${data.destinatarios} destinatarios`);
      navigate('/marketing');
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al enviar')),
  });

  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [field]: e.target.value }));
  const fc = (field: keyof Criterios) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setCriterios(p => ({ ...p, [field]: e.target.value || undefined }));

  async function fetchPreview() {
    setLoadingPreview(true);
    try {
      const clean = cleanCriterios();
      const res = await api.post('/api/marketing/campanas/preview-destinatarios', { criterios: clean });
      setPreviewCount(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al obtener destinatarios'));
    } finally {
      setLoadingPreview(false);
    }
  }

  function cleanCriterios() {
    const c: any = {};
    if (criterios.medicoId) c.medicoId = criterios.medicoId;
    if (criterios.genero) c.genero = criterios.genero;
    if (criterios.edadMin !== '' && criterios.edadMin != null) c.edadMin = Number(criterios.edadMin);
    if (criterios.edadMax !== '' && criterios.edadMax != null) c.edadMax = Number(criterios.edadMax);
    if (criterios.ultimaCitaDesde) c.ultimaCitaDesde = criterios.ultimaCitaDesde;
    if (criterios.ultimaCitaHasta) c.ultimaCitaHasta = criterios.ultimaCitaHasta;
    return c;
  }

  async function handleSubmit(e: React.FormEvent, sendNow: boolean) {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    if (!form.plantillaId) { toast.error('Selecciona una plantilla'); return; }

    const dto: any = {
      nombre: form.nombre,
      plantillaId: form.plantillaId,
      criterios: cleanCriterios(),
    };

    if (!sendNow && enviando === 'programado') {
      if (!form.fechaProgramada) { toast.error('Selecciona fecha y hora de envío'); return; }
      dto.fechaProgramada = new Date(form.fechaProgramada).toISOString();
      dto.estado = 'programada';
    }

    if (sendNow) {
      // Crear y luego enviar
      const campana = await api.post('/api/marketing/campanas', dto).then(r => r.data).catch(() => null);
      if (!campana) return;
      enviarMutation.mutate(campana.id);
    } else {
      // Solo crear (borrador o programada)
      createMutation.mutate(dto);
    }
  }

  const isLoading = createMutation.isPending || enviarMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-text-primary">Nueva campaña</h2>
          <p className="text-sm text-text-secondary">Configura y envía tu campaña de email</p>
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-5">
        {/* Datos básicos */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <h3 className="font-semibold text-text-primary">Datos de la campaña</h3>
          <div>
            <label className={labelCls}>Nombre de la campaña *</label>
            <input className={inputCls} value={form.nombre} onChange={f('nombre')} placeholder="Promoción enero 2026" required />
          </div>
          <div>
            <label className={labelCls}>Plantilla de correo *</label>
            <select className={inputCls} value={form.plantillaId} onChange={f('plantillaId')} required>
              <option value="">Selecciona una plantilla</option>
              {plantillas.map((p: any) => (
                <option key={p.id} value={p.id}>{p.nombre} — {p.asunto}</option>
              ))}
            </select>
            {plantillas.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No tienes plantillas.{' '}
                <button type="button" onClick={() => navigate('/marketing/plantillas/nueva')} className="underline">
                  Crea una ahora
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Destinatarios */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-text-primary">Destinatarios</h3>
            <button
              type="button"
              onClick={() => setShowFiltros(!showFiltros)}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              Filtros avanzados {showFiltros ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>

          {showFiltros && (
            <div className="space-y-3 pt-2 border-t border-gray-50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Médico titular</label>
                  <select className={inputCls} value={criterios.medicoId || ''} onChange={fc('medicoId')}>
                    <option value="">Todos los médicos</option>
                    {medicos.map((m: any) => (
                      <option key={m.id} value={m.id}>{m.nombre} {m.apellidoPaterno}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Género</label>
                  <select className={inputCls} value={criterios.genero || ''} onChange={fc('genero')}>
                    {GENERO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>Rango de edad</label>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={120} className={inputCls} placeholder="Mínima" value={criterios.edadMin ?? ''} onChange={fc('edadMin')} />
                  <span className="text-text-secondary text-sm">–</span>
                  <input type="number" min={0} max={120} className={inputCls} placeholder="Máxima" value={criterios.edadMax ?? ''} onChange={fc('edadMax')} />
                </div>
              </div>

              <div>
                <label className={labelCls}>Última cita entre</label>
                <div className="flex items-center gap-2">
                  <input type="date" className={inputCls} value={criterios.ultimaCitaDesde || ''} onChange={fc('ultimaCitaDesde')} />
                  <span className="text-text-secondary text-sm">y</span>
                  <input type="date" className={inputCls} value={criterios.ultimaCitaHasta || ''} onChange={fc('ultimaCitaHasta')} />
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={fetchPreview}
            disabled={loadingPreview}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-text-secondary hover:bg-gray-50 transition-colors"
          >
            {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
            Ver destinatarios
          </button>

          {previewCount && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-text-primary">
                {previewCount.total} pacientes coinciden · {previewCount.conEmail} con email
              </p>
              {previewCount.preview.length > 0 && (
                <div className="space-y-1">
                  {previewCount.preview.map((p: any, i: number) => (
                    <p key={i} className="text-xs text-text-secondary">{p.nombre} — {p.email}</p>
                  ))}
                  {previewCount.total > 5 && (
                    <p className="text-xs text-text-secondary">...y {previewCount.total - 5} más</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Programación */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <h3 className="font-semibold text-text-primary">Programación</h3>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setEnviando('ahora')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                enviando === 'ahora' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-text-secondary hover:border-gray-300'
              }`}
            >
              <Send size={15} /> Enviar ahora
            </button>
            <button
              type="button"
              onClick={() => setEnviando('programado')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                enviando === 'programado' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-text-secondary hover:border-gray-300'
              }`}
            >
              <Clock size={15} /> Programar
            </button>
          </div>

          {enviando === 'programado' && (
            <div>
              <label className={labelCls}>Fecha y hora de envío *</label>
              <input
                type="datetime-local"
                className={inputCls}
                value={form.fechaProgramada}
                onChange={f('fechaProgramada')}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 border border-gray-200 text-text-secondary py-2.5 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          {enviando === 'programado' ? (
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
              Programar campaña
            </button>
          ) : (
            <>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 border border-gray-200 text-text-secondary py-2.5 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-60 transition-colors"
              >
                Guardar borrador
              </button>
              <button
                type="button"
                disabled={isLoading}
                onClick={(e) => handleSubmit(e as any, true)}
                className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
              >
                {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Crear y enviar
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
